// Utility layer (TS-2.5.04) - pure, no side effects. ProjectRecord's raw* fields are typed
// `object?` in Contracts (RawPercentComplete/RawOriginalGoLive/RawCurrentGoLive/RawRecastCount -
// ProjectRecord.cs) precisely because BRD section 6.1 allows a real number, a numeric string or
// free text on the wire - so a controlled text input needs a STRING projection of the record, and
// this is the one place that projection is built and reversed, rather than every input owning its
// own ad-hoc String(value)/parseInt.
import type { ProjectRecord } from "../../../api/generated/contracts";

export interface ProjectDraft {
  readonly key: string | null;
  readonly businessProjectId: string;
  readonly name: string;
  readonly rawTeam: string;
  readonly owner: string;
  // D-279/D-280: empty means "inherit the assigned product's business owner" - a distinction the
  // form must be able to tell apart from a typed value, which is why this stays a plain string
  // (never pre-filled with the resolved inherited name) rather than something already resolved.
  readonly businessOwner: string;
  readonly description: string;
  readonly rawStatus: string;
  readonly rawPercentComplete: string;
  readonly rawOriginalGoLive: string;
  readonly rawCurrentGoLive: string;
  readonly rawRecastCount: string;
  readonly featureDetails: string;
  readonly subgroup: string;
  readonly productKey: string | null;
  readonly concurrencyToken: string | null;
}

function textOf(value: unknown): string {
  if (value === null || value === undefined) return "";
  return String(value);
}

export const EMPTY_PROJECT_DRAFT: ProjectDraft = {
  key: null,
  businessProjectId: "",
  name: "",
  rawTeam: "",
  owner: "",
  businessOwner: "",
  description: "",
  rawStatus: "",
  rawPercentComplete: "",
  rawOriginalGoLive: "",
  rawCurrentGoLive: "",
  rawRecastCount: "",
  featureDetails: "",
  subgroup: "",
  productKey: null,
  concurrencyToken: null,
};

export function draftFromRecord(record: ProjectRecord): ProjectDraft {
  return {
    key: record.key,
    businessProjectId: record.businessProjectId === null ? "" : String(record.businessProjectId),
    name: record.name,
    rawTeam: record.rawTeam ?? "",
    owner: record.owner ?? "",
    // A null businessOwner IS the inheriting state (D-279), not "nothing typed here yet" - both
    // read as an empty input either way, and the distinction the form still needs (typed vs
    // inherited) is drawn by the DESCRIPTION SLOT ProjectForm.tsx renders beside the empty field,
    // not by the draft string itself.
    businessOwner: record.businessOwner ?? "",
    description: record.description ?? "",
    rawStatus: record.rawStatus ?? "",
    rawPercentComplete: textOf(record.rawPercentComplete),
    rawOriginalGoLive: textOf(record.rawOriginalGoLive),
    rawCurrentGoLive: textOf(record.rawCurrentGoLive),
    rawRecastCount: textOf(record.rawRecastCount),
    featureDetails: record.featureDetails ?? "",
    subgroup: record.subgroup ?? "",
    productKey: record.productKey,
    concurrencyToken: record.concurrencyToken,
  };
}

function blankToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

function blankToNullNumber(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  const parsed = Number(trimmed);
  return Number.isNaN(parsed) ? null : parsed;
}

/** After a successful save, adopts the server's identity (key/concurrencyToken) onto whatever the
 *  CURRENT draft holds - not the pre-save snapshot the request was built from - so an edit typed
 *  while the request was in flight survives, and (this is the defect this function exists to
 *  fix) the concurrencyToken used by the very next save is always the one this save just
 *  received. Leaving the draft's other fields untouched, rather than replacing the whole draft
 *  with `draftFromRecord(saved)`, is what preserves an in-flight edit; calling this via a
 *  functional state update (`setDraft((current) => rebaseDraftAfterSave(current, saved))`) is
 *  what makes "current" mean "as of now" rather than "as of when the save started".
 *
 *  `saved.concurrencyToken` is a FALLBACK, not the primary source: SaveResult<T> carries its own
 *  top-level `concurrencyToken`, and that one - not the token on the returned entity - is this
 *  API's authoritative "the token to use next" (SaveResult.cs's own field, one level up from the
 *  entity it saved). The two happen to agree whenever the entity embeds the post-write row
 *  faithfully, but a caller that read only the entity's token and the entity's token ever lagged
 *  the save result's own for any reason would silently rebase onto a stale value - which is
 *  exactly the stale-token flood this function exists to prevent, so the caller passes the
 *  SaveResult's token explicitly instead of this function reaching into `saved` for it. */
export function rebaseDraftAfterSave(
  current: ProjectDraft,
  saved: ProjectRecord,
  authoritativeToken?: string | null,
): ProjectDraft {
  return {
    ...current,
    key: saved.key,
    concurrencyToken: authoritativeToken ?? saved.concurrencyToken,
  };
}

export function recordFromDraft(draft: ProjectDraft): ProjectRecord {
  return {
    key: draft.key,
    businessProjectId: blankToNullNumber(draft.businessProjectId),
    name: draft.name.trim(),
    rawTeam: blankToNull(draft.rawTeam),
    owner: blankToNull(draft.owner),
    // Blank collapses to null here exactly as it does for every other blankToNull field - and for
    // BusinessOwner that collapse is not incidental, it IS D-279's inheritance switch: null is what
    // tells the server "inherit the product's owner again". The Manager's own InheritWhenBlank
    // would collapse a submitted "" the same way, so sending null directly changes nothing the
    // server does with it - this just avoids sending a value the API would immediately discard.
    businessOwner: blankToNull(draft.businessOwner),
    description: blankToNull(draft.description),
    rawStatus: blankToNull(draft.rawStatus),
    rawPercentComplete: blankToNull(draft.rawPercentComplete),
    rawOriginalGoLive: blankToNull(draft.rawOriginalGoLive),
    rawCurrentGoLive: blankToNull(draft.rawCurrentGoLive),
    rawRecastCount: blankToNull(draft.rawRecastCount),
    featureDetails: blankToNull(draft.featureDetails),
    subgroup: blankToNull(draft.subgroup),
    productKey: draft.productKey,
    concurrencyToken: draft.concurrencyToken,
    updatedAtUtc: null,
    updatedBy: null,
  };
}
